/**
 * Autorización AEAT — `TDS-CORE` Parte 4 §5 y `OD-0031`.
 *
 * Es el paso que hoy impide emitir. La pantalla dice en lenguaje llano cómo
 * está y qué toca hacer ahora:
 *
 * ```text
 * NOT_STARTED        las dos formas, con el mismo peso (D-01). La firma online
 *                    se ve, desactivada: no se ofrece lo que no existe
 * PENDING_SIGNATURE  los pasos de la Sede con el apoderado y el camino oficial
 * PENDING_VALIDATION "lo estamos comprobando", con el plazo del piloto
 * ACTIVE             desde cuándo, y cómo retirarla
 * REJECTED / REVOKED qué pasó y volver a empezar
 * NIF_CHANGED        el poder lo dio otro NIF: hay que autorizar de nuevo
 * ```
 *
 * El usuario nunca la activa: eso lo comprueba una persona de Faktelio en la
 * Sede (`OD-0031` D-05). Aquí sólo declara que ya lo ha hecho.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Autorizacion, type Resultado } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";

/**
 * Una llamada de la API que devuelve la autorización ya actualizada. Se
 * declara como interfaz, no como alias con flecha: el guardrail de textos en
 * JSX (OD-0017 D-07) confunde `=> Promise<...>` con texto suelto.
 */
interface Peticion {
  (): Promise<Resultado<Autorizacion>>;
}

function fechaLegible(iso: string | null): string {
  return iso === null ? "" : new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

export function PantallaAutorizacionAeat({
  hayConexion,
  onSesionCaducada,
  onCambio,
}: {
  hayConexion: boolean;
  onSesionCaducada: () => void;
  /** Para que Inicio y el control previo reflejen el estado nuevo. */
  onCambio: () => void;
}) {
  const [datos, setDatos] = useState<Autorizacion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    setErrorCarga(false);
    const r = await api.autorizacionAeat();
    setCargando(false);
    if (r.ok) setDatos(r.datos);
    else if (r.estado === 401) onSesionCaducada();
    else setErrorCarga(true);
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function accion(hacer: Peticion): Promise<void> {
    setMensaje(null);
    setOcupado(true);
    const r = await hacer();
    setOcupado(false);
    if (r.ok) {
      setDatos(r.datos);
      onCambio();
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje(t.autorizacion.soloPropietario);
    setMensaje(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  if (cargando) return <Tarjeta titulo={t.autorizacion.titulo}><p className="ayuda-neutra">{t.autorizacion.cargando}</p></Tarjeta>;
  if (errorCarga || datos === null) {
    return (
      <Tarjeta titulo={t.autorizacion.titulo}>
        <Aviso tono="error">{t.comun.errorGenerico}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>{t.autorizacion.reintentar}</Boton>
      </Tarjeta>
    );
  }

  const estado = datos.estado;
  const apoderado = `${datos.apoderado.nombre} (NIF ${datos.apoderado.nif})`;

  return (
    <Tarjeta titulo={t.autorizacion.titulo}>
      <p>{t.autorizacion.paraQue}</p>
      {mensaje !== null ? <Aviso tono="error">{mensaje}</Aviso> : null}

      {estado === "NOT_STARTED" || estado === "REJECTED" || estado === "REVOKED" || estado === "NIF_CHANGED" ? (
        <>
          {estado === "REJECTED" ? (
            <Aviso tono="aviso" titulo={t.autorizacion.rechazadaTitulo}>
              {t.autorizacion.motivos[datos.motivoRechazo ?? "OTHER"] ?? t.autorizacion.motivos.OTHER}
            </Aviso>
          ) : null}
          {estado === "REVOKED" ? <Aviso tono="aviso">{t.autorizacion.retirada}</Aviso> : null}
          {estado === "NIF_CHANGED" ? <Aviso tono="aviso">{t.autorizacion.nifCambiado}</Aviso> : null}

          <h3>{t.autorizacion.comoTitulo}</h3>
          <div className="opciones-autorizacion">
            <div className="opcion-autorizacion">
              <h4>{t.autorizacion.ruta1Titulo}</h4>
              <p className="etiqueta-precio">{t.autorizacion.gratis}</p>
              <p>{t.autorizacion.ruta1Detalle}</p>
              <Boton
                onClick={() => void accion(() => api.elegirAutorizacion("APODERAMIENTO"))}
                deshabilitado={ocupado || !hayConexion}
              >
                {t.autorizacion.empezar}
              </Boton>
            </div>
            <div className="opcion-autorizacion opcion-desactivada">
              <h4>{t.autorizacion.ruta3Titulo}</h4>
              <p className="etiqueta-precio">{t.autorizacion.ruta3Precio}</p>
              <p>{t.autorizacion.ruta3Detalle}</p>
              <Boton variante="secundario" deshabilitado>{t.autorizacion.masAdelante}</Boton>
            </div>
          </div>
        </>
      ) : null}

      {estado === "PENDING_SIGNATURE" ? (
        <>
          <h3>{t.autorizacion.pasosTitulo}</h3>
          <ol className="pasos-autorizacion">
            <li>{t.autorizacion.paso1}</li>
            <li>{t.autorizacion.paso2}</li>
            <li>{t.autorizacion.paso3(apoderado)}</li>
            <li>{t.autorizacion.paso4}</li>
          </ol>
          {/*
            A LA GUIA ANTES QUE A LA SEDE, y por el mismo motivo que la puerta
            lleva a ella: en la Sede hay que saber QUE tramites marcar, y
            marcar el que no es deja un poder que PARECE bueno y con el que
            todo fallaria. Hasta el 2026-10-15 esta pestaña no enlazaba a la
            guia, asi que quien llegaba por Configuracion -y no por la puerta-
            se quedaba con el resumen de cuatro lineas.

            Enlace de verdad, no un boton que navega, para que la pestaña nueva
            no la bloquee el navegador. Y la ruta es la MISMA clave que usa la
            puerta: escrita dos veces podria divergir.
          */}
          <p>
            <a href={t.puerta.rutaComoAutorizar} target="_blank" rel="noopener noreferrer">
              {t.autorizacion.guiaTexto}
            </a>
          </p>
          <p>
            <a href={t.autorizacion.enlaceSede} target="_blank" rel="noopener noreferrer">
              {t.autorizacion.enlaceSedeTexto}
            </a>
          </p>
          <Aviso tono="info">{t.autorizacion.avisoPoder}</Aviso>
          <Boton onClick={() => void accion(() => api.declararAutorizacion())} deshabilitado={ocupado || !hayConexion}>
            {t.autorizacion.yaLoHeHecho}
          </Boton>
        </>
      ) : null}

      {estado === "PENDING_VALIDATION" ? (
        <Aviso tono="info" titulo={t.autorizacion.comprobandoTitulo}>
          {t.autorizacion.comprobando}
        </Aviso>
      ) : null}

      {estado === "ACTIVE" ? (
        <>
          <Aviso tono="exito" titulo={t.autorizacion.activaTitulo}>
            {t.autorizacion.activa(fechaLegible(datos.validadaEl))}
          </Aviso>
          <p className="ayuda-neutra">{t.autorizacion.retirarAviso}</p>
          <Boton
            variante="secundario"
            onClick={() => void accion(() => api.retirarAutorizacion())}
            deshabilitado={ocupado || !hayConexion}
          >
            {t.autorizacion.retirar}
          </Boton>
        </>
      ) : null}
    </Tarjeta>
  );
}
